import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from '@/hooks/useAnalytics';
import { LandingButton } from './LandingButton';

const CheckIcon = ({ size = 13, className = '' }: { size?: number; className?: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

interface PlanConfig {
  slug: 'starter' | 'professional' | 'business' | 'enterprise';
  priceMonthly?: number;
  priceAnnual?: number;
  currency?: string;
  ctaHref: string;
  ctaVariant: 'default' | 'outline' | 'gradient';
  highlight: boolean;
  hasBadge?: boolean;
  firstFeatureIsHeader: boolean;
}

const PLAN_CONFIGS: PlanConfig[] = [
  {
    slug: 'starter',
    priceMonthly: 0,
    priceAnnual: 0,
    currency: '€',
    ctaHref: '/auth?tab=signup',
    ctaVariant: 'outline',
    highlight: false,
    firstFeatureIsHeader: false,
  },
  {
    slug: 'professional',
    priceMonthly: 49,
    priceAnnual: 39,
    currency: '€',
    ctaHref: '/auth?tab=signup',
    ctaVariant: 'gradient',
    highlight: true,
    hasBadge: true,
    firstFeatureIsHeader: true,
  },
  {
    slug: 'business',
    priceMonthly: 129,
    priceAnnual: 99,
    currency: '€',
    ctaHref: '/auth?tab=signup',
    ctaVariant: 'default',
    highlight: false,
    firstFeatureIsHeader: true,
  },
  {
    slug: 'enterprise',
    ctaHref: '#',
    ctaVariant: 'outline',
    highlight: false,
    firstFeatureIsHeader: true,
  },
];

interface PlanI18n {
  name: string;
  tagline: string;
  limit: string;
  cta: string;
  features: string[];
  priceCustom?: string;
}

export const PricingSection = React.memo(() => {
  const { t } = useTranslation();
  const [annual, setAnnual] = useState(false);
  const { track } = useAnalytics();

  return (
    <section id="precos" className="py-24 px-6 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500">{t('landing.pricing.eyebrow')}</span>
          <h2
            className="text-3xl md:text-5xl font-medium mt-3 mb-5"
            style={{
              background: 'linear-gradient(to bottom, #000000, #000000, rgba(0, 0, 0, 0.6))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.04em',
            }}
          >
            {t('landing.pricing.titleLine1')}
            <br />
            {t('landing.pricing.titleLine2')}
          </h2>
          <p className="text-base max-w-2xl mx-auto mb-8 text-gray-500">{t('landing.pricing.subtitle')}</p>
          <div className="inline-flex items-center gap-1 p-1 rounded-full border border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={() => setAnnual(false)}
              className={`text-xs font-medium px-4 py-1.5 rounded-full transition-all ${!annual ? 'bg-white text-black shadow-sm' : 'text-gray-600'}`}
            >
              {t('landing.pricing.toggleMonthly')}
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              className={`text-xs font-medium px-4 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${annual ? 'bg-white text-black shadow-sm' : 'text-gray-600'}`}
            >
              {t('landing.pricing.toggleAnnual')}
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-black">{t('landing.pricing.toggleAnnualBadge')}</span>
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {PLAN_CONFIGS.map((cfg) => {
            const planI18n = t(`landing.pricing.plans.${cfg.slug}`, { returnObjects: true }) as PlanI18n;
            const isExternal = cfg.ctaHref === '#';
            const onCtaClick = () => track('landing_cta_clicked', { location: 'pricing', plan: cfg.slug });
            const ctaButton = (
              <LandingButton
                type="button"
                variant={cfg.highlight ? 'default' : cfg.ctaVariant}
                size="sm"
                className={`w-full rounded-lg mb-6 ${cfg.highlight ? 'bg-white text-black hover:bg-gray-100 hover:scale-[1.02] active:scale-[0.98] shadow-md' : ''}`}
              >
                {planI18n.cta}
              </LandingButton>
            );

            return (
              <div
                key={cfg.slug}
                className={`relative rounded-2xl p-7 transition-all ${cfg.highlight ? 'bg-black text-white border border-black shadow-xl shadow-black/10 lg:scale-[1.03]' : 'bg-white border border-gray-200 hover:border-gray-300 hover:shadow-lg hover:shadow-black/5'}`}
              >
                {cfg.hasBadge && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-semibold uppercase tracking-wider px-3 py-1 rounded-full bg-white text-black border border-gray-200 shadow-sm">
                    {t('landing.pricing.popularBadge')}
                  </span>
                )}
                <div className="mb-5">
                  <h3 className={`text-lg font-semibold mb-1 ${cfg.highlight ? 'text-white' : 'text-black'}`}>{planI18n.name}</h3>
                  <p className={`text-xs leading-relaxed ${cfg.highlight ? 'text-white/60' : 'text-gray-500'}`}>{planI18n.tagline}</p>
                </div>
                <div className="mb-5 min-h-[60px]">
                  {planI18n.priceCustom ? (
                    <div className={`text-3xl font-semibold ${cfg.highlight ? 'text-white' : 'text-black'}`}>{planI18n.priceCustom}</div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className={`text-sm ${cfg.highlight ? 'text-white/60' : 'text-gray-500'}`}>{cfg.currency}</span>
                      <span className={`text-4xl font-semibold tracking-tight ${cfg.highlight ? 'text-white' : 'text-black'}`}>
                        {annual ? cfg.priceAnnual : cfg.priceMonthly}
                      </span>
                      <span className={`text-xs ${cfg.highlight ? 'text-white/60' : 'text-gray-500'}`}>{t('landing.pricing.perMonth')}</span>
                    </div>
                  )}
                  <div className={`text-xs mt-2 ${cfg.highlight ? 'text-white/60' : 'text-gray-500'}`}>{planI18n.limit}</div>
                </div>

                {isExternal ? (
                  <a href={cfg.ctaHref} onClick={onCtaClick}>
                    {ctaButton}
                  </a>
                ) : (
                  <Link to={cfg.ctaHref} onClick={onCtaClick}>
                    {ctaButton}
                  </Link>
                )}

                <ul className="space-y-2.5">
                  {planI18n.features.map((f, idx) => (
                    <li key={f} className={`flex items-start gap-2 text-xs ${cfg.highlight ? 'text-white/80' : 'text-gray-600'}`}>
                      <CheckIcon size={13} className={`mt-0.5 flex-shrink-0 ${cfg.highlight ? 'text-white' : 'text-black'}`} />
                      {idx === 0 && cfg.firstFeatureIsHeader ? <span className="font-medium">{f}</span> : f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});
PricingSection.displayName = 'PricingSection';
